/**
 * Error Boundary Component
 * Catches JavaScript errors and displays fallback UI
 */

import React, { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to Sentry
    Sentry.withScope(scope => {
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
      });
      scope.setTag('errorBoundary', true);
      Sentry.captureException(error);
    });

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <Card.Content>
              <div className="text-center">
                <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-xl font-semibold text-gray-900 mb-2">
                  Algo salió mal
                </h1>
                <p className="text-gray-600 mb-6">
                  Se produjo un error inesperado. Nuestro equipo ha sido
                  notificado automáticamente.
                </p>

                <div className="space-y-3">
                  <Button
                    onClick={this.handleReset}
                    variant="primary"
                    fullWidth
                  >
                    Intentar de nuevo
                  </Button>
                  <Button
                    onClick={this.handleReload}
                    variant="outline"
                    fullWidth
                  >
                    Recargar página
                  </Button>
                </div>

                {this.props.showDetails &&
                  import.meta.env.DEV &&
                  this.state.error && (
                    <details className="mt-6 text-left">
                      <summary className="text-sm text-gray-500 cursor-pointer">
                        Detalles técnicos
                      </summary>
                      <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 overflow-auto max-h-40">
                        <div className="mb-2">
                          <strong>Error:</strong> {this.state.error.message}
                        </div>
                        <div className="mb-2">
                          <strong>Stack:</strong>
                          <pre className="whitespace-pre-wrap">
                            {this.state.error.stack}
                          </pre>
                        </div>
                        {this.state.errorInfo && (
                          <div>
                            <strong>Component Stack:</strong>
                            <pre className="whitespace-pre-wrap">
                              {this.state.errorInfo.componentStack}
                            </pre>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
              </div>
            </Card.Content>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC version for easier use
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

// Sentry's Error Boundary (alternative)
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export { ErrorBoundary };
